import {
    ExpandMoreRounded,
    ThumbDownOutlined,
    ThumbDownRounded,
    ThumbUpOutlined,
    ThumbUpRounded,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Collapse,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import {
    KboFlowFeedback,
    KboFlowFeedbackSentiment,
    KboFlowFeedbackService,
    KboFlowFeedbackStep,
} from "services/KboFlowFeedbackService";
import { kboFlowRunDetailColors as pageColors } from "./KboFlowRunDetailStyles";

/**
 * Human feedback on a KBo Flow question and on the individual pipeline steps
 * taken for it. Question feedback accumulates as a history; step feedback keeps
 * one row per user per question+step and is updated in place.
 */

/**
 * Which persistence scope a feedback panel writes to. `question` appends to a
 * history; `step` keeps one row per user per question+step and updates it in
 * place, so that panel prefills with the current user's existing entry.
 */
type FeedbackScope =
    | { kind: "question" }
    | { kind: "step"; step: KboFlowFeedbackStep };

/**
 * The existing feedback schema is question scoped. Keep the branch/step provenance
 * durable by embedding this compact trace in the append-only comment body instead of
 * routing branch reviews through the single-row-per-phase endpoint.
 */
export type KboFlowFeedbackTrace = {
    runId?: string;
    nodeId?: string;
    findingId?: string;
    resultId?: string;
    pathNodeIds?: string[];
    step: string;
    skills?: string[];
};

const tracePrefix = "[KBO_FLOW_TRACE";

function decodeTraceValue(value: string) {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

function traceIdentity(trace: KboFlowFeedbackTrace) {
    return [
        trace.runId,
        trace.nodeId,
        trace.findingId,
        trace.resultId,
        (trace.pathNodeIds ?? []).join(","),
        trace.step,
    ]
        .map((value) => encodeURIComponent(value ?? "-"))
        .join("|");
}

function withTrace(
    text: string,
    trace: KboFlowFeedbackTrace,
    questionId: string,
) {
    const value = (item?: string) => encodeURIComponent(item || "-");
    const skills =
        (trace.skills ?? []).map(encodeURIComponent).join(",") || "-";
    return `${tracePrefix} id=${traceIdentity(trace)} run=${value(
        trace.runId,
    )} question=${value(questionId)} node=${value(
        trace.nodeId,
    )} finding=${value(trace.findingId)} result=${value(
        trace.resultId,
    )} path=${value((trace.pathNodeIds ?? []).join(","))} step=${value(
        trace.step,
    )} skills=${skills}]\n${text}`;
}

function splitTrace(text?: string) {
    if (!text?.startsWith(tracePrefix)) return { text, traceId: undefined };
    const newline = text.indexOf("\n");
    const header = newline === -1 ? text : text.slice(0, newline);
    const id = /\bid=([^\s\]]+)/.exec(header)?.[1];
    const read = (key: string) => {
        const raw = new RegExp(`\\b${key}=([^\\s\\]]+)`).exec(header)?.[1];
        return raw && raw !== "-" ? decodeTraceValue(raw) : undefined;
    };
    const skills = read("skills");
    return {
        text: newline === -1 ? "" : text.slice(newline + 1),
        traceId: id,
        context: {
            runId: read("run"),
            questionId: read("question"),
            nodeId: read("node"),
            findingId: read("finding"),
            resultId: read("result"),
            pathNodeIds: read("path"),
            step: read("step"),
        },
        skills: skills && skills !== "-" ? skills.split(",") : [],
    };
}

/**
 * MUI darkens a contained button to `palette.<color>.dark` on hover, and this
 * theme's success/error dark tones are near-black. Hold the selected reaction on
 * its main tone and dim it slightly instead.
 */
function selectedReactionSx(color: "success" | "error") {
    return {
        "&:hover": { bgcolor: `${color}.main`, filter: "brightness(0.94)" },
    };
}

function formatFeedbackTimestamp(value: string) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
        ? value
        : new Intl.DateTimeFormat(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
          }).format(parsed);
}

export function StepReviewPanel({
    questionId,
    scope,
    label = "Review this step",
    placeholder = "Add commentary — what is wrong, missing, or worth keeping about this step.",
    defaultExpanded = false,
    standalone = false,
    trace,
}: {
    questionId: string;
    scope: FeedbackScope;
    label?: string;
    placeholder?: string;
    defaultExpanded?: boolean;
    /** Set when the panel sits on its own surface rather than at the foot of a card. */
    standalone?: boolean;
    /** Persists branch-level provenance in question feedback without a schema migration. */
    trace?: KboFlowFeedbackTrace;
}) {
    const service = useMemo(() => new KboFlowFeedbackService(), []);
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [items, setItems] = useState<KboFlowFeedback[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>();
    const [reaction, setReaction] = useState<KboFlowFeedbackSentiment>();
    const [commentary, setCommentary] = useState("");
    const [pendingDeleteId, setPendingDeleteId] = useState<string>();
    const [deleting, setDeleting] = useState(false);
    // Set as soon as the reviewer touches the form, so a GET that lands after they
    // started typing does not overwrite what they wrote.
    const edited = useRef(false);

    const step = scope.kind === "step" ? scope.step : undefined;

    // A step panel edits the current user's single row in place, so it is pulled
    // out of the list and into the form instead of being shown twice.
    const traceId = trace ? traceIdentity(trace) : undefined;
    const scopedItems = traceId
        ? items.filter((item) => splitTrace(item.text).traceId === traceId)
        : items;
    const mine = step ? scopedItems.find((item) => item.isMine) : undefined;
    const listed = step
        ? scopedItems.filter((item) => !item.isMine)
        : scopedItems;

    useEffect(() => {
        if (!expanded) return undefined;
        const controller = new AbortController();
        setError(undefined);
        const request = step
            ? service.getStepFeedback(questionId, step, controller.signal)
            : service.getQuestionFeedback(questionId, controller.signal);
        request
            .then((list) => {
                if (controller.signal.aborted) return;
                setItems(list ?? []);
                setLoaded(true);
                if (edited.current) return;
                const existing = step
                    ? (list ?? []).find((item) => item.isMine)
                    : undefined;
                setReaction(existing?.sentiment);
                setCommentary(existing?.text ?? "");
            })
            .catch((caught: unknown) => {
                if (controller.signal.aborted) return;
                setError(
                    caught instanceof Error
                        ? caught.message
                        : "Feedback could not be loaded.",
                );
            });
        return () => controller.abort();
        // `loaded` is intentionally excluded: refetch is driven by expanding and
        // by the explicit setItems updates below, not by load bookkeeping.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded, questionId, service, step]);

    const submit = async () => {
        if (!reaction) return;
        setSaving(true);
        setError(undefined);
        try {
            const body = {
                sentiment: reaction,
                text: trace
                    ? withTrace(commentary.trim(), trace, questionId)
                    : commentary.trim() || undefined,
            };
            const saved = step
                ? await service.submitStepFeedback(questionId, step, body)
                : await service.submitQuestionFeedback(questionId, body);
            setItems((current) => [
                saved,
                ...current.filter((item) => item.id !== saved.id),
            ]);
            edited.current = false;
            if (!step) {
                setReaction(undefined);
                setCommentary("");
            }
        } catch (caught: unknown) {
            setError(
                caught instanceof Error
                    ? caught.message
                    : "Feedback could not be saved.",
            );
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!pendingDeleteId) return;
        setDeleting(true);
        setError(undefined);
        try {
            if (step) {
                await service.deleteStepFeedback(
                    questionId,
                    step,
                    pendingDeleteId,
                );
            } else {
                await service.deleteQuestionFeedback(
                    questionId,
                    pendingDeleteId,
                );
            }
            const removedWasMine = items.find(
                (item) => item.id === pendingDeleteId,
            )?.isMine;
            setItems((current) =>
                current.filter((item) => item.id !== pendingDeleteId),
            );
            if (step && removedWasMine) {
                edited.current = false;
                setReaction(undefined);
                setCommentary("");
            }
            setPendingDeleteId(undefined);
        } catch (caught: unknown) {
            setError(
                caught instanceof Error
                    ? caught.message
                    : "Feedback could not be deleted.",
            );
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ mt: standalone ? 0 : 1.5 }}>
            <Box
                component="button"
                type="button"
                onClick={() => setExpanded((value) => !value)}
                sx={{
                    alignItems: "center",
                    bgcolor: "transparent",
                    border: 0,
                    borderTop: standalone ? 0 : "1px dashed",
                    borderColor: "divider",
                    color: pageColors.muted,
                    cursor: "pointer",
                    display: "flex",
                    font: "inherit",
                    fontSize: 11,
                    fontWeight: 800,
                    gap: 0.5,
                    letterSpacing: "0.04em",
                    p: 0,
                    pt: standalone ? 0 : 1.5,
                    textTransform: "uppercase",
                    width: "100%",
                }}
            >
                <ExpandMoreRounded
                    sx={{
                        fontSize: 16,
                        transform: expanded ? "rotate(180deg)" : "none",
                        transition: "transform 120ms",
                    }}
                />
                {label}
                {loaded && scopedItems.length > 0 && (
                    <Box component="span" sx={{ opacity: 0.7 }}>
                        · {scopedItems.length}
                    </Box>
                )}
            </Box>
            <Collapse in={expanded} timeout={180} mountOnEnter>
                <Box sx={{ mt: 1.25 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 1.25 }}>
                            {error}
                        </Alert>
                    )}
                    {listed.length > 0 && (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.25,
                                mb: 1.5,
                            }}
                        >
                            {listed.map((item) => (
                                <Box
                                    key={item.id}
                                    sx={{
                                        alignItems: "flex-start",
                                        display: "flex",
                                        gap: 1.25,
                                    }}
                                >
                                    {item.sentiment === "Positive" ? (
                                        <ThumbUpRounded
                                            fontSize="small"
                                            color="success"
                                            sx={{ mt: 0.25 }}
                                        />
                                    ) : (
                                        <ThumbDownRounded
                                            fontSize="small"
                                            color="error"
                                            sx={{ mt: 0.25 }}
                                        />
                                    )}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                color: pageColors.muted,
                                                fontSize: 12,
                                            }}
                                        >
                                            {item.authorName ?? "Unknown user"}{" "}
                                            ·{" "}
                                            {formatFeedbackTimestamp(
                                                item.createdOn,
                                            )}
                                        </Typography>
                                        {item.text && (
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    whiteSpace: "pre-wrap",
                                                }}
                                            >
                                                {splitTrace(item.text).text}
                                            </Typography>
                                        )}
                                        {splitTrace(item.text).traceId ? (
                                            <Typography
                                                sx={{
                                                    color: pageColors.muted,
                                                    fontSize: 11,
                                                    mt: 0.25,
                                                }}
                                            >
                                                Context captured · run{" "}
                                                {splitTrace(item.text).context
                                                    ?.runId ?? "—"}{" "}
                                                · question{" "}
                                                {splitTrace(item.text).context
                                                    ?.questionId ?? "—"}{" "}
                                                · finding{" "}
                                                {splitTrace(item.text).context
                                                    ?.findingId ?? "—"}{" "}
                                                · node{" "}
                                                {splitTrace(item.text).context
                                                    ?.nodeId ?? "—"}{" "}
                                                · result{" "}
                                                {splitTrace(item.text).context
                                                    ?.resultId ?? "—"}{" "}
                                                · path{" "}
                                                {splitTrace(item.text).context
                                                    ?.pathNodeIds ?? "—"}{" "}
                                                ·{" "}
                                                {splitTrace(item.text).context
                                                    ?.step ?? "review"}{" "}
                                                · skills:{" "}
                                                {splitTrace(
                                                    item.text,
                                                ).skills?.join(", ") || "none"}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                    {item.isMine && (
                                        <Button
                                            size="small"
                                            color="inherit"
                                            onClick={() =>
                                                setPendingDeleteId(item.id)
                                            }
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                            size="small"
                            variant={
                                reaction === "Positive"
                                    ? "contained"
                                    : "outlined"
                            }
                            color={
                                reaction === "Positive" ? "success" : "inherit"
                            }
                            sx={
                                reaction === "Positive"
                                    ? selectedReactionSx("success")
                                    : undefined
                            }
                            startIcon={
                                reaction === "Positive" ? (
                                    <ThumbUpRounded />
                                ) : (
                                    <ThumbUpOutlined />
                                )
                            }
                            onClick={() => {
                                edited.current = true;
                                setReaction((value) =>
                                    value === "Positive"
                                        ? undefined
                                        : "Positive",
                                );
                            }}
                        >
                            Looks right
                        </Button>
                        <Button
                            size="small"
                            variant={
                                reaction === "Negative"
                                    ? "contained"
                                    : "outlined"
                            }
                            color={
                                reaction === "Negative" ? "error" : "inherit"
                            }
                            sx={
                                reaction === "Negative"
                                    ? selectedReactionSx("error")
                                    : undefined
                            }
                            startIcon={
                                reaction === "Negative" ? (
                                    <ThumbDownRounded />
                                ) : (
                                    <ThumbDownOutlined />
                                )
                            }
                            onClick={() => {
                                edited.current = true;
                                setReaction((value) =>
                                    value === "Negative"
                                        ? undefined
                                        : "Negative",
                                );
                            }}
                        >
                            Needs work
                        </Button>
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder={placeholder}
                        value={commentary}
                        onChange={(event) => {
                            edited.current = true;
                            setCommentary(event.target.value);
                        }}
                        sx={{ mt: 1.25 }}
                    />
                    <Box
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                            mt: 1,
                        }}
                    >
                        {mine && (
                            <Typography
                                sx={{
                                    color: pageColors.muted,
                                    fontSize: 12,
                                    mr: "auto",
                                }}
                            >
                                Your review, saved{" "}
                                {formatFeedbackTimestamp(mine.createdOn)}
                            </Typography>
                        )}
                        {mine && (
                            <Button
                                size="small"
                                color="inherit"
                                disabled={saving}
                                onClick={() => setPendingDeleteId(mine.id)}
                            >
                                Delete
                            </Button>
                        )}
                        <Button
                            size="small"
                            disabled={saving || (!reaction && !commentary)}
                            onClick={() => {
                                edited.current = false;
                                setReaction(mine?.sentiment);
                                setCommentary(mine?.text ?? "");
                            }}
                        >
                            Reset
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={saving || !reaction}
                            onClick={submit}
                        >
                            {mine ? "Update feedback" : "Save feedback"}
                        </Button>
                    </Box>
                </Box>
            </Collapse>

            <AcxConfirmationDialog
                isOpen={!!pendingDeleteId}
                title="Delete feedback?"
                content="This permanently removes the feedback entry."
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
                isLoading={deleting}
                onClose={() => setPendingDeleteId(undefined)}
                onCancel={() => setPendingDeleteId(undefined)}
                onConfirm={confirmDelete}
            />
        </Box>
    );
}
